import { isPlatformBrowser, NgIf, NgStyle, NgClass, TitleCasePipe, CurrencyPipe } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output, PLATFORM_ID, Inject, HostListener, OnDestroy } from '@angular/core';

//* Material
import { MatDialog } from '@angular/material/dialog';

//* Components
import { ProductNoteComponent } from '../product-note/product-note.component';

//* Models
import { Item, TProductTypeQuantity } from '@models/cart.model';

//* Services
import { ProductsService } from '@services/products.service';
import { CartService } from '@services/cart.service';
import { CartObserverService } from '@services/cart-observer.service';
import { SnackbarService } from '@services/snackbar.service';

//* Shared
import { imageHandling } from '@shared/imageHandling';
import { ProductsValidationsService, TProductType } from '@services/products-validations.service';
import { Subject, debounceTime, takeUntil } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { RouterLink } from '@angular/router';
import { NgxMaskDirective } from 'ngx-mask';
import { SmartLabelComponent } from '../smart-label/smart-label.component';
import { SupermixApplyLabelComponent } from '../supermix-apply-label/supermix-apply-label.component';
import { SupermixListsComponent } from 'src/app/supermix-lists/supermix-lists.component';
import { Store } from '@ngrx/store';
import { AppStateCart } from 'src/app/state/cart/cart.reducer';
import { MatTooltipModule } from '@angular/material/tooltip';

declare let gtag: any;

@Component({
  selector: 'app-product-cart',
  templateUrl: './product-cart.component.html',
  styleUrls: ['./product-cart.component.scss'],
  standalone: true,
  imports: [NgIf, RouterLink, NgStyle, SupermixApplyLabelComponent, MatTooltipModule, SmartLabelComponent, MatFormFieldModule, MatButtonModule, MatIconModule, NgClass, FormsModule, MatProgressSpinnerModule, TitleCasePipe, CurrencyPipe, TranslateModule, NgxMaskDirective]
})
export class ProductCartComponent implements OnInit, OnDestroy {

  @Input() product: Item;
  @Input() isFromCart: boolean;

  @Output() adding = new EventEmitter();
  @Output() set = new EventEmitter();
  @Output() subtracting = new EventEmitter();
  private subs = new Subject<void>();
  public discount = 0;
  public storeId: string;
  public baseGrams = 0;
  public showIfDontExistProducts = false;
  public isDecimal: boolean;
  public loading = false;
  private clickSubject = new Subject();
  public productImageCloudFront: string;
  public isMobile: boolean;
  public loadingSupermix = false;
  @HostListener('window:resize', ['$event'])
  onResize(): void {
    this.isMobile = window.innerWidth <= 800;
  }
  public productImage: string;
  public quantity: number;
  public productQuantity: number;
  public productType: TProductType;
  public productTypeQuantity: TProductTypeQuantity;
  public showSpinner: boolean;
  private superMixItems: any[];
  public cartItems: Item[] = [];
  constructor(
    private dialog: MatDialog,
    private cartService: CartService,
    private cartObserver: CartObserverService,
    @Inject(PLATFORM_ID) private platformId: string,
    private productService: ProductsService,
    private snackbarService: SnackbarService,
    private productsValidationsService: ProductsValidationsService,
    private storeCart: Store<AppStateCart>
  ) {
    if (isPlatformBrowser(this.platformId)) {
      this.storeId = JSON.parse(localStorage.getItem('address') || '{}').store;
    }
  }


  ngOnDestroy(): void {
    this.subs.next();
    this.subs.complete();
  }

  ngOnInit(): void {
    this.isMobile = window.innerWidth <= 800;
    this.loading = true;
    this.product.can_replace = true;
    const imgHa = imageHandling(this.product.image_url, 'small', '.webp');
    this.productImage = imgHa;
    const format = this.product.format.toUpperCase();
    this.product.format = format === 'KG' ? 'KG' : 'PZ';
    const descriptionArr = this.product.name.split(' ');
    const desc = descriptionArr.join('-');
    const arrWithoutSlash = desc.split('/');
    this.product.product_url = `/producto/${arrWithoutSlash.join('-').toLowerCase()}-${this.product.item_id}`;
    this.discount = 100 - Math.trunc((this.product.price * 100) / this.product.regular_price);
    const unit = this.product?.unit.toLowerCase();
    this.productType = this.productsValidationsService.productCartTypeValidation(unit, this.product.weight);
    switch (this.productType) {
      case 'grammage':
        this.productTypeQuantity = 'grammage';
        this.productQuantity = this.product?.quantity * 1000;
        break;
      case 'pza':
        this.productTypeQuantity = 'pza';
        this.productQuantity = this.product.quantity;
        break;
      case 'granel':
        this.productTypeQuantity = 'granel';
        this.baseGrams = 50;
        this.productQuantity = this.product?.quantity * 1000;
        break;
      case 'pza-grammage':
        this.baseGrams = Number.parseFloat(this.product?.weight) * 1000;

        if (unit === 'grammage') {
          this.productQuantity = this.product?.quantity * 1000;
          this.productTypeQuantity = 'grammage';
        } else {
          this.productTypeQuantity = 'pza';
          this.productQuantity = this.product.quantity / Number.parseFloat(this.product.weight);
          this.productQuantity = Number.parseInt(this.productQuantity.toFixed(2));
        }
        break;
    }

    this.sendQuantityListener();
    this.setLocalCartListener();
    this.loading = false;
  }


  private sendQuantityListener(): void {
    this.clickSubject.pipe(
      debounceTime(600)
    ).subscribe(async (data) => {
      if (data === 'skip') {
        this.showSpinner = false;
        return;
      }
      await this.sendQuantityChange();
    });
  }

  public onClickProduct(): void {
    if (isPlatformBrowser(this.platformId)) {
      window.scroll(0, 0);
    }
  }

  public onAddingNote(): void {
    this.dialog.open(ProductNoteComponent, {
      data: this.product,
      panelClass: 'as-product-note-container'
    }).afterClosed().subscribe(response => {
      if (response) {
        this.product.comment = response.data.item.comment;
      }
    });
  }

  public async changeGrams(event: any): Promise<void> {
    try {
      if (event) {
        let eventParse: number;
        let multiple: number;
        switch (this.productTypeQuantity) {
          case 'grammage':
            eventParse = parseInt(event);
            multiple = eventParse / Number.parseFloat(this.product?.weight);
            this.quantity = Math.round(multiple) * Number.parseFloat(this.product?.weight) / 1000;
            break;
          case 'granel':
            eventParse = parseInt(event);
            multiple = eventParse / 1000 / 0.05;
            this.quantity = Math.round(multiple) * 0.05;
            break;
        }

        this.showSpinner = true;
        this.clickSubject.next(null);
      }
    } catch (error: any) {
      this.showSpinner = false;
      if (error?.status === 404) {
        this.snackbarService.openSnackbar('GENERAL.stockError', 'warn');
      }
    }
  }

  public setQuantityProduct() {
    switch (this.productType) {
      case 'grammage':
      case 'granel':
        this.productQuantity = this.quantity * 1000;
        break;
      case 'pza':
        this.productQuantity = this.quantity;
        break;
      case 'pza-grammage':
        if (this.product?.unit.toLowerCase() === 'grammage') {
          this.productQuantity = Number.parseInt((this.quantity * 1000).toFixed(2));
        } else {
          this.productQuantity = this.product.quantity / Number.parseFloat(this.product.weight);
          this.productQuantity = Number.parseInt(this.productQuantity.toFixed(2));
        }
        break;
    }
  }

  public async changePieces(event: any): Promise<void> {
    try {
      if (event) {
        switch (this.productType) {
          case 'pza':
            this.quantity = parseInt(event);
            break;
          case 'pza-grammage':
            this.quantity = parseInt(event) * Number.parseFloat(this.product?.weight);
            break;
        }
        this.showSpinner = true;
        this.clickSubject.next(null);
      }
    } catch (error: any) {
      this.showSpinner = false;
      if (error?.status === 404) {
        this.snackbarService.openSnackbar('GENERAL.stockError', 'warn');
      }
    }
  }

  public async add(): Promise<void> {

    try {
      const stock = this.product.stock;
      if (this.productTypeQuantity === 'pza') {
        if (this.productType === 'pza') {
          this.product.quantity += 1;
          if (this.product.quantity > stock) {
            this.product.quantity -= 1;
            return;
          }
          this.quantity = this.product.quantity;
        } else if (this.productType === 'pza-grammage') {
          this.product.quantity += Number.parseFloat(this.product?.weight);
          if (this.product.quantity > stock) {
            this.product.quantity -= Number.parseFloat(this.product?.weight);
            return;
          }
          this.quantity = this.product.quantity;
        }
      } else if (this.productTypeQuantity === 'granel' || this.productTypeQuantity === 'grammage') {
        if (this.productType === 'grammage' || this.productType === 'pza-grammage') {
          this.product.quantity += Number.parseFloat(this.product?.weight);
          if (this.product.quantity > stock) {
            this.product.quantity -= Number.parseFloat(this.product?.weight);
            return;
          }
          this.quantity = Number.parseFloat(this.product.quantity.toFixed(2));
        } else if (this.productType === 'granel') {
          this.product.quantity += 0.05;
          if (this.product.quantity > stock) {
            this.product.quantity -= 0.05;
            return;
          }
          this.quantity = Number.parseFloat(this.product.quantity.toFixed(2));
        }
      }
      this.setQuantityProduct();
      this.clickSubject.next(null);
    } catch (error: any) {
      if (error?.status === 404) {
        this.snackbarService.openSnackbar('GENERAL.stockError', 'warn');
      }
    }
  }

  public async sendQuantityChange(): Promise<void> {
    try {
      this.showSpinner = true;
      const { data } = await this.cartService.onHandlingProductQuantity({
        itemId: this.product.item_id,
        quantity: Math.round(this.quantity * 1000) / 1000,
        branch_id: this.storeId,
        unit: this.productTypeQuantity,
        variant: this.product.variant ?? ''
      });
      if (data?.message === 'Se ha actualizado la cantidad') {
      } else {
        this.snackbarService.openSnackbar('GENERAL.stockError', 'warn');
      }
      this.onEmitReload();
    } catch (error) {
      this.loading = false;
      this.showSpinner = false;
      this.snackbarService.openSnackbar('GENERAL.stockError', 'warn');
    }
  }

  public subtract(): void {
    try {
      let access = false;
      if (this.quantity === 0) {
        return;
      }
      if (this.productTypeQuantity === 'pza') {
        if (this.productType === 'pza') {
          access = true;
          this.product.quantity -= 1;
          this.quantity = this.product.quantity;
        } else if (this.productType === 'pza-grammage') {
          access = true;
          this.product.quantity -= Number.parseFloat(this.product?.weight);
          this.quantity = this.product.quantity;
        }
      } else if (this.productTypeQuantity === 'granel' || this.productTypeQuantity === 'grammage') {
        if (this.productType === 'grammage' || this.productType === 'pza-grammage') {
          access = true;
          this.product.quantity -= Number.parseFloat(this.product?.weight);
          this.quantity = this.product.quantity;
        } else if (this.productType === 'granel') {
          if (this.product?.quantity >= 0.05) {
            access = true;
            this.product.quantity -= 0.05;
            this.quantity = Number.parseFloat(this.product.quantity.toFixed(2));
          }
        }
      }
      this.setQuantityProduct();
      if (this.quantity <= 0) {
        this.onRemoveProduct();
        return;
      }
      if (access) {
        this.clickSubject.next(null);
      }
    } catch (error: any) {
      if (error?.status === 404) {
        this.snackbarService.openSnackbar('GENERAL.stockError', 'warn');
      }
    }
  }


  public async onHandlingProductReplacement(): Promise<void> {
    await this.cartService.onHandlingProductReplacement({ itemId: this.product.item_id });
  }

  public async onRemoveProduct(): Promise<void> {
    try {
      this.clickSubject.next('skip');
      this.showSpinner = true;
      this.product.quantity = 0; //Necesario al eliminar para detectar la eliminacion en las otras clases
      await this.cartService.onHandlingRemoveProduct(this.product.item_id);
      // const res = await this.cartService.onGettingCartListDirect();
      // if (res.data.items.length === 0) {
      //   await this.orderService.onAddingTip({ amount: 0 });
      // }
      this.onEmitReload();
      /* GOOGLE ANALYTICS REMOVE ITEM FROM CART TRACKING*/
      if (isPlatformBrowser(this.platformId)) {
        gtag('event', 'remove_from_cart', {
          items: [
            {
              id: this.product?.item_id?.toString(),
              name: this.product.name,
              brand: this.product.name,
              variant: this.product?.price ?? '',
              quantity: this.product.quantity,
              price: this.product.price.toString()
            }
          ]
        });
      }
      /* GOOGLE ANALYTICS REMOVE ITEM FROM CART TRACKING*/
    } catch (error) {
      this.showSpinner = false;
      this.onEmitReload();
    }
  }

  public onEmitReload(): void {
    this.cartObserver.cart$.emit(true);
  }

  public async onSwitchTypeQuanity(type: TProductTypeQuantity): Promise<void> {
    if (this.productTypeQuantity === type) return;
    if (type === 'pza') {
      this.quantity = this.productQuantity / 1000;
      this.productQuantity = this.productQuantity / (Number.parseFloat(this.product?.weight) * 1000);
    } else {
      this.productQuantity = Number.parseFloat(((Number.parseFloat(this.product?.weight) * this.productQuantity) * 1000).toFixed(2));
      this.quantity = Number.parseFloat((this.productQuantity / 1000).toFixed(2));
    }
    this.productTypeQuantity = type;
    this.clickSubject.next(null);
  }

  public toFloat(value: string): number {
    return parseFloat(value);
  }

  public async showMore() {
    if (!this.loadingSupermix) {
      this.loadingSupermix = true;
      if (!this.superMixItems) {
        const { data } = await this.productService.getSupermixProducts(this.product?.comodin?.toString(), this.storeId);
        this.superMixItems = data[0].items;
      }
      this.product.super_mix = this.superMixItems;
      this.product.event_name = this.superMixItems[0].event_name;
      this.supermixCartSet();
      this.loadingSupermix = false;
      this.dialog.open(SupermixListsComponent, {
        panelClass: 'as-supermix-list',
        width: this.isMobile ? '100vw' : '730px',
        height: this.isMobile ? '100vh' : 'auto',
        maxWidth: this.isMobile ? '100vw' : '800px',
        data: { product: this.product },
        autoFocus: false
      });

    }
  }


  public supermixCartSet(): void {
    if (this.cartItems?.length && this.product.super_mix.length) {
      this.cartItems.forEach((item: any) => {
        this.product.super_mix.forEach((supermixItem: any) => {
          if (item.item_id === supermixItem.id) {
            supermixItem.productFromCart = item;
          }
        });
      });
    }
  }

  public setLocalCartListener(): void {
    this.storeCart.select('cart').pipe(takeUntil(this.subs)).subscribe(({ info }) => {
      this.showSpinner = false;
    });
  }


}




